// The camera's target, and the two matrices it hands the shaders.
//
// The fast client REUSES `render/camera.ts` verbatim (the plan's C.1). That
// camera touches only `view.x`, `view.y`, `view.scale.x`, `view.scale.set()`
// and `shakeView.x/y`, so a plain object with those five members is all it
// needs. `View` is that object, plus the three read-outs the engine and the
// layers want: the clip matrix, the inverse for picking, and the visible
// world rect.
//
// `render/camera.ts` states both interfaces itself (R431): it takes a
// structural `PanZoomView` rather than a scene graph's `Container`, so the fast
// client hands it this object with no cast at all. The two types are
// re-exported here, because a fast-client module that needs them reads them
// beside the object that satisfies them.

import type { CameraUniforms, ViewRect } from './layer';
import type { PanZoomView, ShakeView } from '../render/camera';

export type { PanZoomView, ShakeView };

export class View implements PanZoomView {
  /** The pan, in CSS px: where world (0,0) sits on screen. */
  x = 0;
  y = 0;
  /** The zoom: CSS px for one world px. */
  readonly scale: { x: number; set(v: number): void };
  /**
   * The quake shudder, in CSS px (R101).
   *
   * It rides in BOTH matrices, so a shaken frame is drawn shaken, and it
   * stays out of `viewRect` — the pan clamp of `render/camera.ts`, the
   * viewport report and the screen-to-world picking must never see a
   * tremor.
   */
  readonly shake: ShakeView = { x: 0, y: 0 };

  /**
   * The arrival's zoom nudge (R838): a scale `k` about the screen anchor
   * `(ax, ay)`, each a fraction of the drawing surface.
   *
   * Rise from the Deep opens the map slightly too large and lets it settle,
   * which is the card's own claim that the view ascends. Like the shudder it
   * rides in BOTH matrices and stays out of `viewRect` and `screenToWorld`,
   * so the pan clamp, the viewport report and the picking never see it.
   * `k` 1 is the steady state, and the layer resets it on its finish.
   */
  readonly nudge = { k: 1, ax: 0.5, ay: 0.5 };

  private readonly viewProj = new Float32Array(9);
  private readonly viewInv = new Float32Array(9);
  private readonly cam: CameraUniforms;

  constructor() {
    // `set` writes the field the camera reads back, so one zoom lives in one
    // place. A PixiJS `ObservablePoint` behaves the same way.
    const scale = { x: 1, set(v: number) { scale.x = v; } };
    this.scale = scale;
    this.cam = {
      viewProj: this.viewProj,
      viewInv: this.viewInv,
      zoom: 1, dpr: 1, pxPerWorld: 1,
      w: 1, h: 1, shakeX: 0, shakeY: 0,
    };
  }

  /**
   * Fills the camera uniforms for one frame. The object and both arrays are
   * reused, so a frame allocates nothing.
   *
   * `w`/`h` are the drawing surface in CSS px and `dpr` is device px for one
   * CSS px.
   */
  uniforms(w: number, h: number, dpr: number): CameraUniforms {
    // The nudge scales about its own screen anchor, so the point under the
    // anchor holds still while the rest of the picture grows out from it.
    const n = this.nudge;
    const k = n.k;
    const s = this.scale.x * k;
    const axp = n.ax * w;
    const ayp = n.ay * h;
    const tx = axp + (this.x + this.shake.x - axp) * k;
    const ty = ayp + (this.y + this.shake.y - ayp) * k;
    writeViewProj(this.viewProj, s, tx, ty, w, h);
    writeViewInv(this.viewInv, s, tx, ty, h, dpr);
    const cam = this.cam;
    cam.zoom = s;
    cam.dpr = dpr;
    cam.pxPerWorld = s * dpr;
    cam.w = w;
    cam.h = h;
    cam.shakeX = this.shake.x;
    cam.shakeY = this.shake.y;
    return cam;
  }

  /** The visible world rect, without the shake, into a reused object. */
  viewRect(out: ViewRect, w: number, h: number): ViewRect {
    const s = this.scale.x;
    out.x0 = -this.x / s;
    out.y0 = -this.y / s;
    out.x1 = (w - this.x) / s;
    out.y1 = (h - this.y) / s;
    return out;
  }

  /**
   * CSS px to world px, without the shake. It is the one conversion the
   * pointer path of `main.ts` makes (`toWorld`), so a click lands where the
   * player aimed even while the ground is shaking.
   */
  screenToWorld(cssX: number, cssY: number, out: { x: number; y: number }): { x: number; y: number } {
    const s = this.scale.x;
    out.x = (cssX - this.x) / s;
    out.y = (cssY - this.y) / s;
    return out;
  }

  /**
   * World px to CSS px, without the shake: the inverse of `screenToWorld`.
   * The peek's leader starts at the mark of the object it describes (round
   * 084 card 02).
   */
  worldToScreen(x: number, y: number, out: { x: number; y: number }): { x: number; y: number } {
    const s = this.scale.x;
    out.x = x * s + this.x;
    out.y = y * s + this.y;
    return out;
  }
}

/**
 * World px to clip space, column-major (the plan's A.6).
 *
 * `clip.x = 2·(s·X + tx)/W − 1` and `clip.y = 1 − 2·(s·Y + ty)/H`, which is
 * the CSS screen position turned into clip space with y down.
 */
export function writeViewProj(
  out: Float32Array, s: number, tx: number, ty: number, w: number, h: number,
): Float32Array {
  out[0] = (2 * s) / w; out[1] = 0; out[2] = 0;
  out[3] = 0; out[4] = (-2 * s) / h; out[5] = 0;
  out[6] = (2 * tx) / w - 1; out[7] = 1 - (2 * ty) / h; out[8] = 1;
  return out;
}

/**
 * Device px to world px, column-major (the plan's A.6).
 *
 * The input is the `gl_FragCoord` convention: device px with y counted up
 * from the bottom of the drawing surface.
 */
export function writeViewInv(
  out: Float32Array, s: number, tx: number, ty: number, h: number, dpr: number,
): Float32Array {
  const k = 1 / (dpr * s);
  out[0] = k; out[1] = 0; out[2] = 0;
  out[3] = 0; out[4] = -k; out[5] = 0;
  out[6] = -tx / s; out[7] = (h - ty) / s; out[8] = 1;
  return out;
}

/** Applies a column-major mat3 to a point. For tests and for picking. */
export function applyMat3(
  m: Float32Array, x: number, y: number,
): { x: number; y: number } {
  return {
    x: m[0] * x + m[3] * y + m[6],
    y: m[1] * x + m[4] * y + m[7],
  };
}
